// ============================================================
// Tie-In — OnboardingFlow.jsx
// 5-step onboarding for the triage module.
// Step 5 = Gmail + Calendar OAuth connect.
// Saves config to user_triage_config on completion.
// ============================================================

import { useState } from 'react';
import { t } from './triageTheme.js';
import { submitOnboarding } from './triageApi.js';

const STEPS = [
  { id: 'role',      label: 'Your role',        sub: 'We use this to calibrate how your emails are prioritized' },
  { id: 'windows',   label: 'Your schedule',    sub: 'When should calendar blocks be placed?' },
  { id: 'overrides', label: 'Priority accounts',sub: 'Certain senders always go straight to top priority' },
  { id: 'draft',     label: 'Your voice',       sub: 'How should your draft replies sound?' },
  { id: 'connect',   label: 'Connect accounts', sub: 'Gmail and Google Calendar access is required to run triage' },
];

const ROLES = [
  { val: 'sales_rep',                   label: 'Service Sales Rep' },
  { val: 'roof_replacement_sales_rep',  label: 'Replacement Sales Rep' },
  { val: 'service_manager',             label: 'Service Manager' },
  { val: 'office_admin',                label: 'Office Admin' },
  { val: 'finance',                     label: 'Finance' },
  { val: 'leadership',                  label: 'Leadership' },
  { val: 'owner',                       label: 'Owner' },
];

const DRAFT_MODES = [
  { val: 'auto',          label: 'Drafts + calendar blocks', sub: 'Full automation — draft replies and calendar reminders' },
  { val: 'calendar_only', label: 'Calendar blocks only',     sub: 'Reminders only, I\'ll write my own replies' },
  { val: 'off',           label: 'Classify only',            sub: 'Sort my inbox, nothing else' },
];

// ── Step primitives ───────────────────────────────────────────

function OptionButton({ label, sub, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display:      'flex',
        alignItems:   'center',
        width:        '100%',
        padding:      '12px 14px',
        background:   selected ? t.colors.accentGlow : t.colors.bgPanel,
        border:       `1px solid ${selected ? t.colors.accent : t.colors.border}`,
        borderRadius: t.radii.lg,
        cursor:       'pointer',
        marginBottom: '8px',
        gap:          '12px',
        textAlign:    'left',
        transition:   t.transitions.fast,
      }}
    >
      <div style={{
        width:        '18px',
        height:       '18px',
        borderRadius: '50%',
        border:       `2px solid ${selected ? t.colors.accent : t.colors.border}`,
        background:   selected ? t.colors.accent : 'transparent',
        flexShrink:   0,
        position:     'relative',
        transition:   t.transitions.fast,
      }}>
        {selected && (
          <div style={{
            position: 'absolute', top: '3px', left: '3px',
            width: '8px', height: '8px', borderRadius: '50%', background: '#fff',
          }} />
        )}
      </div>
      <div>
        <div style={{ fontSize: '14px', fontWeight: 500, color: t.colors.textPrimary, fontFamily: t.fonts.body }}>
          {label}
        </div>
        {sub && (
          <div style={{ fontSize: '12px', color: t.colors.textSecondary, fontFamily: t.fonts.body, marginTop: '2px' }}>
            {sub}
          </div>
        )}
      </div>
    </button>
  );
}

function TimeRangePicker({ label, startKey, endKey, values, onChange }) {
  const times = [];
  for (let h = 6; h <= 20; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      times.push(`${hh}:${mm}`);
    }
  }

  const inputStyle = {
    background:   t.colors.bgInput,
    border:       `1px solid ${t.colors.border}`,
    borderRadius: t.radii.md,
    color:        t.colors.textPrimary,
    fontFamily:   t.fonts.mono,
    fontSize:     '14px',
    padding:      '10px 12px',
    flex:         1,
    outline:      'none',
  };

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ fontSize: '12px', color: t.colors.textSecondary, fontFamily: t.fonts.body, marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <select value={values[startKey] || ''} onChange={e => onChange(startKey, e.target.value)} style={inputStyle}>
          <option value="">Start</option>
          {times.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <span style={{ color: t.colors.textMuted, fontFamily: t.fonts.mono, fontSize: '12px' }}>–</span>
        <select value={values[endKey] || ''} onChange={e => onChange(endKey, e.target.value)} style={inputStyle}>
          <option value="">End</option>
          {times.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
    </div>
  );
}

function ConnectButton({ service, connected, onClick }) {
  const isGmail = service === 'gmail';
  const icon = isGmail ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );

  return (
    <button
      onClick={connected ? undefined : onClick}
      style={{
        display:      'flex',
        alignItems:   'center',
        width:        '100%',
        padding:      '14px 16px',
        background:   connected ? t.colors.successDim : t.colors.bgPanel,
        border:       `1px solid ${connected ? t.colors.success + '66' : t.colors.border}`,
        borderRadius: t.radii.lg,
        cursor:       connected ? 'default' : 'pointer',
        gap:          '12px',
        marginBottom: '10px',
        textAlign:    'left',
        transition:   t.transitions.fast,
      }}
    >
      <div style={{ color: connected ? t.colors.success : t.colors.textSecondary }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: 500, fontFamily: t.fonts.body,
          color: connected ? t.colors.success : t.colors.textPrimary }}>
          {isGmail ? 'Gmail' : 'Google Calendar'}
        </div>
        <div style={{ fontSize: '12px', color: t.colors.textSecondary, fontFamily: t.fonts.body, marginTop: '2px' }}>
          {connected
            ? 'Connected'
            : isGmail ? 'Read inbox, create drafts, manage labels'
            : 'Place calendar blocks for action items'}
        </div>
      </div>
      {connected ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={t.colors.success} strokeWidth="2.5">
          <path d="M20 6L9 17l-5-5"/>
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.colors.textMuted} strokeWidth="2">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      )}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────
export default function OnboardingFlow({ userId, onComplete }) {
  const [step,    setStep]    = useState(0);
  const [answers, setAnswers] = useState({
    role:             '',
    draft_mode:       'auto',
    draft_voice_note: '',
    maintenance_email:'',
    eod_override_domains:    [],
    this_week_override_domains: [],
    eod_block_start_morning:    '08:00',
    eod_block_end_morning:      '09:00',
    eod_block_start_midday:     '16:00',
    eod_block_end_midday:       '17:00',
    eod_block_start_afternoon:  '08:00',
    eod_block_end_afternoon:    '09:00',
  });
  const [gmailConnected,    setGmailConnected]    = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);

  function setAnswer(key, val) {
    setAnswers(prev => ({ ...prev, [key]: val }));
  }

  function setTimeWindow(key, val) {
    setAnswers(prev => ({ ...prev, [key]: val }));
  }

  async function finish() {
    setSaving(true);
    try {
      await submitOnboarding(userId, answers);
      onComplete?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const canAdvance = () => {
    if (step === 0) return !!answers.role;
    if (step === 4) return gmailConnected && calendarConnected;
    return true;
  };

  // Progress bar
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 16px 24px', overflowY: 'auto' }}>

      {/* Progress */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.mono }}>
            STEP {step + 1} OF {STEPS.length}
          </span>
          <span style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.mono }}>
            {Math.round(progress)}%
          </span>
        </div>
        <div style={{ height: '3px', background: t.colors.border, borderRadius: t.radii.pill }}>
          <div style={{ height: '100%', width: `${progress}%`, background: t.colors.accent,
            borderRadius: t.radii.pill, transition: t.transitions.normal }} />
        </div>
      </div>

      {/* Step heading */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '22px', fontWeight: 600, fontFamily: t.fonts.display,
          color: t.colors.textPrimary, letterSpacing: '0.02em', marginBottom: '4px' }}>
          {STEPS[step].label}
        </div>
        <div style={{ fontSize: '13px', color: t.colors.textSecondary, fontFamily: t.fonts.body }}>
          {STEPS[step].sub}
        </div>
      </div>

      {/* Step content */}
      <div style={{ flex: 1 }}>

        {/* Step 0: Role */}
        {step === 0 && ROLES.map(r => (
          <OptionButton
            key={r.val}
            label={r.label}
            selected={answers.role === r.val}
            onClick={() => setAnswer('role', r.val)}
          />
        ))}

        {/* Step 1: Scheduling windows */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: '12px', color: t.colors.textMuted, fontFamily: t.fonts.body,
              marginBottom: '14px', lineHeight: 1.5 }}>
              The agent runs at 7:05am, 12:03pm, and 5:05pm. Calendar blocks for your EOD items
              will be placed in the window closest to each run.
            </div>
            <TimeRangePicker label="Morning window (7am run)" startKey="eod_block_start_morning"   endKey="eod_block_end_morning"   values={answers} onChange={setTimeWindow} />
            <TimeRangePicker label="Midday window (12pm run)" startKey="eod_block_start_midday"    endKey="eod_block_end_midday"    values={answers} onChange={setTimeWindow} />
            <TimeRangePicker label="Afternoon window (5pm run)" startKey="eod_block_start_afternoon" endKey="eod_block_end_afternoon" values={answers} onChange={setTimeWindow} />
          </div>
        )}

        {/* Step 2: Priority overrides */}
        {step === 2 && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: t.colors.textPrimary,
                fontFamily: t.fonts.body, marginBottom: '6px' }}>
                Always EOD — domains or names
              </div>
              <div style={{ fontSize: '12px', color: t.colors.textSecondary, fontFamily: t.fonts.body, marginBottom: '8px' }}>
                Emails from these senders always go to top priority, regardless of content.
              </div>
              <textarea
                rows={3}
                placeholder="e.g. midwestpm.com, John Smith"
                defaultValue={(answers.eod_override_domains || []).join(', ')}
                onBlur={e => setAnswer('eod_override_domains', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                style={{
                  width: '100%', background: t.colors.bgInput, border: `1px solid ${t.colors.border}`,
                  borderRadius: t.radii.md, color: t.colors.textPrimary, fontFamily: t.fonts.body,
                  fontSize: '13px', padding: '10px 12px', resize: 'none', outline: 'none', lineHeight: 1.5,
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: t.colors.textPrimary,
                fontFamily: t.fonts.body, marginBottom: '6px' }}>
                Always This Week — domains or names
              </div>
              <div style={{ fontSize: '12px', color: t.colors.textSecondary, fontFamily: t.fonts.body, marginBottom: '8px' }}>
                These senders will never be escalated above This Week.
              </div>
              <textarea
                rows={3}
                placeholder="e.g. newsletters.com, Vendor Name"
                defaultValue={(answers.this_week_override_domains || []).join(', ')}
                onBlur={e => setAnswer('this_week_override_domains', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                style={{
                  width: '100%', background: t.colors.bgInput, border: `1px solid ${t.colors.border}`,
                  borderRadius: t.radii.md, color: t.colors.textPrimary, fontFamily: t.fonts.body,
                  fontSize: '13px', padding: '10px 12px', resize: 'none', outline: 'none', lineHeight: 1.5,
                }}
              />
            </div>
          </div>
        )}

        {/* Step 3: Draft voice */}
        {step === 3 && (
          <div>
            {DRAFT_MODES.map(m => (
              <OptionButton
                key={m.val}
                label={m.label}
                sub={m.sub}
                selected={answers.draft_mode === m.val}
                onClick={() => setAnswer('draft_mode', m.val)}
              />
            ))}
            {answers.draft_mode !== 'off' && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: t.colors.textPrimary,
                  fontFamily: t.fonts.body, marginBottom: '6px' }}>
                  Your communication style
                </div>
                <textarea
                  rows={3}
                  placeholder="e.g. Direct and friendly, always offer a site visit. Never use corporate jargon."
                  value={answers.draft_voice_note}
                  onChange={e => setAnswer('draft_voice_note', e.target.value)}
                  style={{
                    width: '100%', background: t.colors.bgInput, border: `1px solid ${t.colors.border}`,
                    borderRadius: t.radii.md, color: t.colors.textPrimary, fontFamily: t.fonts.body,
                    fontSize: '13px', padding: '10px 12px', resize: 'none', outline: 'none', lineHeight: 1.5,
                  }}
                />
                <div style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body, marginTop: '6px' }}>
                  Claude uses this when writing your draft replies
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Connect */}
        {step === 4 && (
          <div>
            <ConnectButton
              service="gmail"
              connected={gmailConnected}
              onClick={() => { window.location.href = `${import.meta.env.VITE_API_URL}/api/oauth/gmail/connect?userId=${userId}`; }}
            />
            <ConnectButton
              service="calendar"
              connected={calendarConnected}
              onClick={() => { window.location.href = `${import.meta.env.VITE_API_URL}/api/oauth/calendar/connect?userId=${userId}`; }}
            />
            {!gmailConnected && (
              <div style={{ fontSize: '12px', color: t.colors.textMuted, fontFamily: t.fonts.body,
                lineHeight: 1.5, marginTop: '8px' }}>
                You'll be redirected to Google to grant access. We only request the minimum
                permissions needed to run triage.
              </div>
            )}
            {gmailConnected && calendarConnected && (
              <div style={{
                padding: '12px 14px', background: t.colors.successDim,
                border: `1px solid ${t.colors.success}44`, borderRadius: t.radii.lg,
                marginTop: '8px', fontSize: '13px', color: t.colors.success, fontFamily: t.fonts.body,
              }}>
                All set — triage will start automatically on your next scheduled run.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ fontSize: '13px', color: t.colors.danger, fontFamily: t.fonts.body,
          padding: '10px 14px', background: t.colors.dangerDim, borderRadius: t.radii.md,
          border: `1px solid ${t.colors.danger}44`, marginBottom: '12px' }}>
          {error}
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
        {step > 0 && (
          <button
            onClick={() => setStep(s => s - 1)}
            style={{
              padding:      '13px 20px',
              background:   'none',
              border:       `1px solid ${t.colors.border}`,
              borderRadius: t.radii.lg,
              color:        t.colors.textSecondary,
              fontSize:     '14px',
              fontFamily:   t.fonts.body,
              cursor:       'pointer',
            }}
          >
            Back
          </button>
        )}
        <button
          onClick={step === STEPS.length - 1 ? finish : () => setStep(s => s + 1)}
          disabled={!canAdvance() || saving}
          style={{
            flex:         1,
            padding:      '13px',
            background:   canAdvance() ? t.colors.accent : t.colors.bgPanel,
            border:       `1px solid ${canAdvance() ? t.colors.accent : t.colors.border}`,
            borderRadius: t.radii.lg,
            color:        canAdvance() ? '#fff' : t.colors.textMuted,
            fontSize:     '14px',
            fontWeight:   500,
            fontFamily:   t.fonts.body,
            cursor:       canAdvance() ? 'pointer' : 'not-allowed',
            transition:   t.transitions.fast,
          }}
        >
          {saving ? 'Saving...' : step === STEPS.length - 1 ? 'Start triage' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
